import React, { useEffect, useState, useContext, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton,
  Table, TableHead, TableBody, TableRow, TableCell, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Chip, LinearProgress, Tooltip, Switch, FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Refresh as RefreshIcon,
  ContentCopy as CopyIcon, AutorenewRounded as RegenerateIcon
} from '@mui/icons-material';
import { ApiContext } from '../contexts/ApiContext';
import { useNotification } from '../contexts/NotificationContext';
import { usePermissions } from '../hooks/usePermissions';

export default function ApiTokensPage() {
  const { apiTokens } = useContext(ApiContext);
  const { showSuccess, showError } = useNotification();
  const { canAction } = usePermissions();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiTokens.getAll();
      setItems(res?.data?.tokens || []);
    } catch (e) {
      showError(`Error cargando tokens: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [apiTokens, showError]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleCreate = async () => {
    try {
      if (!form.name.trim()) {
        showError('El nombre es requerido');
        return;
      }
      await apiTokens.create({ name: form.name, description: form.description });
      showSuccess('Token creado correctamente');
      setOpen(false);
      setForm({ name: '', description: '' });
      await loadAll();
    } catch (e) {
      showError(`Error creando token: ${e.message}`);
    }
  };

  const handleToggle = async (item) => {
    try {
      await apiTokens.update(item.id, { active: !item.active });
      showSuccess(`Token ${item.active ? 'desactivado' : 'activado'}`);
      await loadAll();
    } catch (e) {
      showError(`Error: ${e.message}`);
    }
  };

  const handleRegenerate = async (item) => {
    if (!window.confirm(`Regenerar token "${item.name}"? Las integraciones existentes dejarán de funcionar.`)) return;
    try {
      await apiTokens.regenerate(item.id);
      showSuccess('Token regenerado');
      await loadAll();
    } catch (e) {
      showError(`Error: ${e.message}`);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Eliminar token "${item.name}"? Los webhooks asociados también se eliminarán.`)) return;
    try {
      await apiTokens.delete(item.id);
      showSuccess('Token eliminado');
      await loadAll();
    } catch (e) {
      showError(`Error: ${e.message}`);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSuccess('Copiado al portapapeles');
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('es-ES') : '-';

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">API Tokens</Typography>
            <Box>
              <Button startIcon={<RefreshIcon />} onClick={loadAll} sx={{ mr: 1 }} disabled={loading}>
                Actualizar
              </Button>
              {canAction('create_api_token') && <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
                Nuevo Token
              </Button>}
            </Box>
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          <Paper variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Token</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Requests</TableCell>
                  <TableCell>Creado</TableCell>
                  <TableCell>Ultimo uso</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2">{item.name}</Typography>
                      {item.description && (
                        <Typography variant="caption" color="text.secondary">{item.description}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {item.token.substring(0, 12)}...
                        </Typography>
                        <Tooltip title="Copiar token completo">
                          <IconButton size="small" onClick={() => copyToClipboard(item.token)}>
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.active ? 'Activo' : 'Inactivo'}
                        color={item.active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{item.total_requests || 0}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(item.created_at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(item.last_used_at)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={item.active ? 'Desactivar' : 'Activar'}>
                        <Switch
                          size="small"
                          checked={item.active}
                          onChange={() => handleToggle(item)}
                        />
                      </Tooltip>
                      {canAction('regenerate_api_token') && <Tooltip title="Regenerar token">
                        <IconButton size="small" onClick={() => handleRegenerate(item)}>
                          <RegenerateIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>}
                      {canAction('delete_api_token') && <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>}
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No hay tokens creados</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </CardContent>
      </Card>

      {/* Dialog Crear Token */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo API Token</DialogTitle>
        <DialogContent>
          <TextField
            label="Nombre"
            fullWidth
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
            placeholder="Ej: Integración CRM"
          />
          <TextField
            label="Descripción (opcional)"
            fullWidth
            multiline
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate}>Crear</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
